import {RepositoryFilterProvider, type Repository} from '@github-ui/filter/providers'
import type {FilterQuery, AnyBlock, MutableFilterBlock, FilterConfig, ARIAFilterSuggestion} from '@github-ui/filter'
import {FilterValueType} from '@github-ui/filter'
import {singleValueFilterTypes} from '../SecurityProductsFilterBar'
import {getRepoSuggestionsForOrg} from '../../../services/api'
import type {SimpleRepository} from '../../../types/rules-types'

/** Org-scoped repository filter that uses the repo_suggestions endpoint.
 *  Users type "repo-name" instead of "org/repo-name". */
export class SecurityProductsRepositoryFilterProvider extends RepositoryFilterProvider {
  #repoSuggestionsUrl: string

  constructor(repoSuggestionsUrl: string) {
    super({filterTypes: singleValueFilterTypes})
    this.#repoSuggestionsUrl = repoSuggestionsUrl
  }

  override async getSuggestions(
    filterQuery: FilterQuery,
    filterBlock: AnyBlock | MutableFilterBlock,
    _config: FilterConfig,
    caretIndex?: number | null,
  ) {
    return this.processSuggestions(filterQuery, filterBlock, this.#processSuggestion.bind(this), caretIndex)
  }

  #processSuggestion(repo: Repository, query: string): ARIAFilterSuggestion {
    const {name} = repo

    return this.rankSuggestion(query, {
      type: FilterValueType.Value,
      displayName: name,
      ariaLabel: `${name}, ${this.displayName}`,
      value: name,
      inlineDescription: false,
      icon: this.icon,
    })
  }

  #toRepository(r: SimpleRepository): Repository {
    return {
      name: r.name,
      owner: r.ownerLogin,
      visibility: r.public ? 'public' : 'private',
      nameWithOwner: `${r.ownerLogin}/${r.name}`,
    }
  }

  #fetchRepos(query: string) {
    return getRepoSuggestionsForOrg(this.#repoSuggestionsUrl, {
      query,
      excludePublicRepos: false,
    })
  }

  override async fetchSuggestions(
    value: string,
    _block: AnyBlock | MutableFilterBlock,
    _filterQuery: FilterQuery,
  ): Promise<Repository[] | null> {
    try {
      const repos = await this.#fetchRepos(value)
      return repos.map(r => this.#toRepository(r))
    } catch {
      return null
    }
  }

  override async validateFilterValue(
    value: string,
    _fetchParams?: URLSearchParams,
    _filterQuery?: FilterQuery,
  ): Promise<Repository | Error | null> {
    try {
      const repos = await this.#fetchRepos(value)
      const match = repos.find(r => r.name.toLowerCase() === value.toLowerCase())
      return match ? this.#toRepository(match) : null
    } catch {
      return null
    }
  }
}
